import { Box, Button, Center, Flex, Input, Stack, Text } from '@chakra-ui/react';
import {
  Avatar,
  CameraIcon,
  Card,
  ContentDrawer,
  GroupIcon,
  UploadAvatar,
} from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';

export interface GroupInfoCardProps {
  avatar: string;
  name: string;
  memberCount: number;
  disabledEditor?: boolean;
  onUpdateSave: (name?: string, file?: File | null) => Promise<void>;
  closeAllClick?: () => void;
}

const GroupInfoCard = ({
  avatar,
  name,
  memberCount,
  disabledEditor,
  onUpdateSave,
  closeAllClick,
}: GroupInfoCardProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(name);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarSrc, setAvatarSrc] = useState(avatar);

  const handleAvatarUpdate = useCallback(async (file: File) => {
    setAvatarFile(file);
    setAvatarSrc(URL.createObjectURL(file));
  }, []);

  const onSave = useCallback(async () => {
    setLoading(true);
    await onUpdateSave(value, avatarFile);
    setLoading(false);
  }, [onUpdateSave, value, avatarFile]);

  useEffect(() => {
    setValue(name);
    setAvatarSrc(avatar);
  }, [avatar, name]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setValue(name);
        setAvatarFile(null);
        setAvatarSrc(avatar);
      }
    },
    [avatar, name]
  );

  return (
    <Card>
      <ContentDrawer
        title={t('chatSettings.groupInfoTitle')}
        disabled={disabledEditor}
        onOpenChange={onOpenChange}
        trigger={
          <Flex alignItems="center" gap="2.5" cursor={disabledEditor ? 'default' : 'pointer'}>
            <Avatar
              src={avatar}
              size="xl"
              activeShake={!disabledEditor}
              cursor={disabledEditor ? 'default' : 'pointer'}
            />
            <Stack gap="0.5" minW="0" overflow="hidden">
              <Flex fontSize="md" alignItems="center" gap="0.5">
                <GroupIcon />
                <Text flex="1" fontWeight="semibold" truncate>
                  {name}
                </Text>
              </Flex>
              <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                {t('chat.groupText')} {memberCount} {t('chat.groupMembersText')}
              </Text>
            </Stack>
          </Flex>
        }
        closeAllClick={closeAllClick}
      >
        <Stack pt="2" gap="84px" h="full">
          <Box>
            <Flex alignItems="center" gap="2" justifyContent="space-between">
              <Text>{t('chatSettings.groupAvatarTitle')}</Text>
              <Box>
                <UploadAvatar onChange={handleAvatarUpdate}>
                  <Box pos="relative">
                    <Avatar src={avatarSrc} size="lg" />
                    <Center
                      pos="absolute"
                      top="0"
                      left="0"
                      w="full"
                      h="full"
                      bg="rgba(0,0,0,0.5)"
                      borderRadius="full"
                    >
                      <CameraIcon />
                    </Center>
                  </Box>
                </UploadAvatar>
              </Box>
            </Flex>
            <Box>
              <Text py="2.5" fontSize="xs" fontWeight="medium" color="fg.muted">
                {t('chat.groupName')}
              </Text>
              <Input
                bg="bg"
                borderRadius="xl"
                size="sm"
                maxLength={32}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
            </Box>
          </Box>
          <Center>
            <Button
              minW="116px"
              h="10"
              borderRadius="xl"
              loading={loading}
              onClick={() => onSave()}
            >
              {t('buttons.save')}
            </Button>
          </Center>
        </Stack>
      </ContentDrawer>
    </Card>
  );
};

const MemoizedGroupInfoCard = memo(GroupInfoCard);
export { MemoizedGroupInfoCard as GroupInfoCard };
